<script lang="ts">
import { InputLexicalText } from '#components'

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useReturnBoxCtx() {
  const t2 = useT2({
    "Item doesn't fit": 'Netinka dydis',
    'Item is too large': 'Per didelė',
    'Delivery related issue': 'Su pristatymu susijusi problema',
    'Item is too small': 'Per maža',
    'Bought multiple sizes to try': 'Užsakiau kelis dydžius',
    'Color is not as expected': 'Netinkama spalva',
    'Style is different than expected': 'Netinkamas stilius',
    "Product doesn't match description": 'Neatitinka aprašymo',
    'Fabric quality issues': 'Prastos kokybės medžiaga',
    'Item is defective or damaged': 'Brokuota arba pažeista',
    'Received wrong item': 'Atsiųsta ne ta prekė',
    'Received extra item': 'Atsiųsta papildoma prekė',
    'Quality issues': 'Kokybės trūkumai',
    'Changed my mind': 'Persigalvojau',
    'Not what I wanted': 'Ne to tikėjausi',
    'Other reason': 'Kita priežastis',

    'Return product': 'Grąžinti prekę',
    Reason: 'Priežastis',
    'Why are you returning?': 'Grąžinimo priežastis',
    Quantity: 'Kiekis',
    Comment: 'Komentaras',
    'Product is not yet shipped': 'Prekė dar neišsiųsta',
    'Order return date is due': 'Grąžinimo terminas baigėsi',
    'Item is returned': 'Prekė grąžinta',
    'Return available until': 'Grąžinti galima iki',
    'This product not allowed to be returned': 'Prekės grąžinti negalima',
    Duration: 'Trukmė',
    'View full return policy': 'Grąžinimo taisyklės',
  })

  function createReturnResonTranslate() {
    const reasonMap: Record<string, Parameters<typeof t2>[0]> = {
      // Fit & Size
      WRONG_FIT: "Item doesn't fit",
      TOO_LARGE: 'Item is too large',
      TOO_SMALL: 'Item is too small',
      MULTIPLE_SIZES: 'Bought multiple sizes to try',
      // Product Appearance
      WRONG_COLOR: 'Color is not as expected',
      WRONG_STYLE: 'Style is different than expected',
      NOT_AS_DESCRIBED: "Product doesn't match description",
      FABRIC_QUALITY: 'Fabric quality issues',
      // Product Issues
      DEFECTIVE: 'Item is defective or damaged',
      WRONG_ITEM: 'Received wrong item',
      EXTRA_ITEM: 'Received extra item',
      POOR_QUALITY: 'Quality issues',
      // Delivery & Service
      DELIVERY_ISSUE: 'Delivery related issue',
      // Customer Choice
      CHANGED_MIND: 'Changed my mind',
      NOT_AS_WANTED: 'Not what I wanted',
      // Other
      OTHER: 'Other reason',
    }

    const reasonList: { name: stringI18n; key: string }[] = []

    for (const key in reasonMap) {
      const value = reasonMap[key]
      if (value) reasonList.push({ key, name: t2(value) })
    }

    function translateReason(key: string | None): string | undefined {
      const resonTranlation = reasonMap[key as keyof typeof reasonMap]
      return resonTranlation ? t2(resonTranlation) : undefined
    }

    return { reasonList, reasonMap, translateReason }
  }

  return { t2, createReturnResonTranslate }
}

export type DocInp = {
  active: boolean
  qty: number
  comment: string
  reasonCode: string | None
}
</script>

<script setup lang="ts">
const { t2, createReturnResonTranslate } = useReturnBoxCtx()

const props = defineProps<{
  isView: boolean
  product: gen.SellerOrderProductsListingFragment
  order: gen.SellerOrderDocAndPaymentFragment
  input: gen.InputBuyerReturnProduct | None
}>()

const { myLink } = useLinks()
const inputComment = useTemplateRef('inputComment')
const refButton = useTemplateRef('refButton')
const isOpened = shallowRef(false)
const { reasonList, reasonMap, translateReason } = createReturnResonTranslate()
const durationStr = useDurationTranslate()

const ui = computed(() => {
  const maxQty = helpers.toPositive(props.product.qtyAllowedToReturn, 0)
  const settingsQty: gen.ProductSettingsQtyFragment = {
    ...props.product.listing?.settingsQty,
    min: 1,
    max: maxQty,
  } satisfies gen.ProductSettingsQtyFragment

  return {
    maxQty,
    settingsQty,
  }
})

const [holder, holderSet] = useValueSynced2((): DocInp => {
  const { input } = props
  return {
    active: !!input,
    comment: input?.comment || '',
    qty: helpers.fit(input?.qty || ui.value.maxQty, 0, ui.value.maxQty),
    reasonCode: input?.reason.code,
  }
})

const isON = computed(() => !!(holder.value?.active || isOpened.value))

function toggleChekbox() {
  if (props.isView) return
  if (holder.value?.active) holderSet({ ...holder.value, active: false })
  else isOpened.value = true
}

function qtyChange(args: '-' | '+') {
  const qty = holder.value?.qty
  if (!qty) return
  const next = helpers.toPositive(qty, 1) + (args === '+' ? 1 : -1)
  if (next <= ui.value.maxQty && next >= 1) {
    // value.value.qty = next
    holderSet({ ...holder.value, qty: next })
  }
}

const reason = computed(() => {
  return {
    disabled: !holder.value || props.isView,
    reasonCode: holder.value?.reasonCode,
  }
})

const activeLabel = computed((): string | undefined => translateReason(reason.value.reasonCode) || reason.value.reasonCode || undefined)

function selectReason(key: string | undefined) {
  holderSet({ ...holder.value, reasonCode: key, active: true })
  isOpened.value = false
}

const returnErr = computed(() => {
  const product = props.product
  if (product.isAllowToReturn) return

  const returnUntil = helpers.toDate(product.returnUntil)
  const returnDoc = product.ref.returnDoc
  const dateDue = !returnUntil || returnUntil < new Date()

  const delivery = props.order.uiBox?.status?.status.delivery
  return {
    noQty: !dateDue && product.qtyAllowedToReturn < 1,
    returnDoc,
    returnUntil,
    dateDue,
    statusNotAllowed: !helpers.isIn(delivery, 'Delivered', 'Shipped', 'ReadyForCollection') || delivery === 'Returned',
  }
})

defineExpose({
  out(): gen.InputBuyerReturnProduct | undefined {
    const val = holder.value
    if (!val?.active) return
    const reasonI18nKey = val.reasonCode ? reasonMap[val.reasonCode] : undefined
    return {
      productId: props.product.ref.product.id,
      qty: val?.qty,
      comment: getFormValueOne(inputComment.value, () => ''),
      reason: {
        code: val.reasonCode || '',
        txt: {
          translations: [
            { lang: 'en', value: reasonI18nKey ? t2(reasonI18nKey, 'en') : '' },
            { lang: 'lt', value: reasonI18nKey ? t2(reasonI18nKey, 'lt') : '' },
          ],
        },
      },
    }
  },
})
</script>

<template>
  <div :class="[$style.cont, { [$style.view]: isView, [$style.empty]: !isON }]">
    <div :class="$style.left">
      <OrderProduct :product="product" skip-link />
    </div>

    <div :class="$style.right">
      <div v-if="product.isAllowToReturn" :class="$style.inputCont">
        <div v-if="isView" :class="$style.checkbox" class="_row _ctrY _fw-xl _fs09">{{ t2('Return product') }}</div>
        <ButtonSwitch v-else :class="$style.checkbox" :txt="t2('Return product')" :model-value="isON" @update:model-value="() => toggleChekbox()" />

        <UiGrow :open="isON">
          <ButtonOption
            ref="refButton"
            :glow="isOpened"
            :on="isOpened"
            right
            :disabled="reason.disabled"
            :icon-txt="t2('Update')"
            open-update
            :class="$style.btnOpen"
            :click="() => (isOpened = !reason.disabled && !isOpened)"
          >
            <label :class="$style.label">{{ t2('Reason') }}</label>
            <div class="_txt" :class="[$style.reasonValue, !activeLabel && '_c4']">{{ activeLabel || t2('Why are you returning?') }}</div>
          </ButtonOption>

          <div :class="$style.btnCont">
            <label :class="$style.label">{{ t2('Quantity') }}</label>

            <div class="_relative _pl04 _flex">
              <ProductBtnBuyInputInput
                :settings-qty="ui.settingsQty"
                :class="$style.qtyInput"
                :stock="ui.maxQty"
                :disabled="!holder || isView"
                :value="holder?.qty || ui.maxQty"
                :value-set="(qty) => holderSet({ ...holder, qty })"
              />
              <IconSlash force-slash :red="false" :class="$style.qtyMax" :curr="undefined" :max="ui.maxQty" />
            </div>

            <ButtonPlus type="minus" size="m" mark :class="$style.add" :disabled="!holder?.qty || holder.qty < 2" @click.passive="qtyChange('-')" />
            <ButtonPlus type="plus" size="m" :class="$style.add" mark :disabled="!holder?.qty || holder.qty >= ui.maxQty" @click.passive="qtyChange('+')" />
          </div>

          <div v-if="!isView || !!holder?.comment" :class="$style.btnContText">
            <template v-if="isView">
              <div v-if="holder?.comment" class="_txt" :class="$style.comment">{{ holder?.comment }}</div>
            </template>
            <InputLexicalText v-else ref="inputComment" :value="holder?.comment" :label="t2('Comment')" class="_hm60" optional />
          </div>
        </UiGrow>

        <UiPreview4 :elem="() => refButton" :model-value="isOpened && !reason.disabled" nopad dropdown @update:model-value="isOpened = false">
          <UiScrollBox :class="$style.drop">
            <ButtonSelect v-for="item in reasonList" :key="item.name" :on="false" :click="() => selectReason(item.key)">{{ item.name }}</ButtonSelect>
          </UiScrollBox>
        </UiPreview4>
      </div>

      <div v-else>
        <div v-if="returnErr?.noQty" class="_label">{{ t2('Item is returned') }}</div>
        <template v-else>
          <UiLine v-if="returnErr?.returnDoc?.returnsNotAllowed" :label="undefined" :value="t2('This product not allowed to be returned')" />
          <div v-else-if="returnErr?.dateDue" class="_label">{{ t2('Order return date is due') }}</div>
          <div v-else-if="returnErr?.statusNotAllowed" class="_label">{{ t2('Product is not yet shipped') }}</div>

          <UiLine :label="t2('Return available until')" :value="returnErr?.returnUntil" :ui="(i) => helpers.uiDate(i)" />
          <UiLine :label="t2('Duration')" :value="returnErr?.returnDoc?.duration" :ui="(i) => durationStr(i) ?? ''" />
        </template>

        <template v-if="returnErr?.returnDoc && !returnErr?.noQty">
          <div v-if="returnErr?.returnDoc?.title.value" class="_label _pb04 _pt2">{{ returnErr?.returnDoc?.title.value }}</div>
          <div v-if="returnErr?.returnDoc?.message.value" class="_short _pb2">{{ returnErr?.returnDoc?.message.value }}</div>
          <LinkUi arrow :to="myLink('info-returns', {})" :txt="t2('View full return policy')" />
        </template>
      </div>
    </div>
  </div>
</template>

<style module>
.cont {
  composes: global(_pt2);
  composes: global(_pb2);
  composes: global(_grid);
  composes: global(_b5);
  composes: global(_bw100);
  grid-template: max-content / 50% 50%;
}
:global(.m) .cont {
  grid-template: max-content / 1fr;
}
.cont:last-of-type {
  border-bottom: none;
}
.inputCont {
  composes: global(_col);
  composes: global(_br14);
  composes: global(_b5);
}
.checkbox {
  composes: global(_h50);
}
.right .checkbox {
  padding: 2px 0 0 14px;
  z-index: 1;
}
.view .checkbox {
  cursor: not-allowed;
}
.btnOpen {
  composes: global(_b5);
  composes: global(_mb-1);
  composes: global(_bw10);
  composes: global(_pt14);
  composes: global(_pb08);
}
.btnCont {
  composes: global(_row);
  composes: global(_fw-l);
  composes: global(_ctrY);
  composes: global(_relative);
  composes: global(_hm50);
  composes: global(_w100p);
  composes: global(_gap04);
  composes: global(_mb-1);
  composes: global(_bw10);

  composes: global(_pt1);
  composes: global(_pr14);
  composes: global(_pb02);
  composes: global(_pl14);

  composes: global(_b5);
  composes: global(_spaced);
}
.btnCont:last-child {
  border-bottom-color: transparent;
}
.view .btnCont:first-child {
  border-top-color: transparent;
}
.btnContText {
  composes: btnCont;
  composes: global(_stretch);

  composes: global(_pr14);
  composes: global(_pl14);
}

.label {
  composes: global(_absolute);
  composes: global(_fs08);
  composes: global(_fw-l);
  composes: global(_lh1);
  composes: global(_c2);
  left: 14px;
  top: 2px;
  z-index: 1;
}
.qtyInput {
  text-align: left;
}
.qtyMax {
  composes: global(_absolute);
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_h100p);

  composes: global(_noselect);

  composes: global(_mta);
  composes: global(_mba);
  composes: global(_mla);
  composes: global(_mra);

  composes: global(_c4);

  top: 0;
  bottom: 0;

  z-index: 1;
  right: 10px;
  word-spacing: 0px;
}
.btnCont:disabled {
  cursor: not-allowed;
}
.reasonValue {
  composes: global(_pt04);
  composes: global(_lh11);
  composes: global(_fs09);
  composes: global(_fw-l);

  color: var(--blue2);
}
.empty .btnCont {
  color: var(--black-4);
  cursor: not-allowed;
}
.drop {
  composes: global(_col);
  composes: global(_pt1);
  composes: global(_pb3);
  composes: global(_stretch);
}
.left {
  composes: global(_col);
  composes: global(_gap4);
  composes: global(_pr14);
  composes: global(_pl14);
}
.right {
  composes: global(_c1);
  composes: global(d_pr14);
  composes: global(d_pl14);
  composes: global(m_pr04);
  composes: global(m_pl04);
}
.view .add {
  display: none;
  visibility: hidden;
}
.comment {
  composes: global(_w100p);
  composes: global(_wmin0);
  composes: global(_ws_pre);
}
</style>
